// Relay frames → conversation state, as a pure transition (docs/10's state-machine rule
// applied to the client). CEK unwrapping is asynchronous WebCrypto, so it happens outside
// and comes back as the `cek` action; events that arrived first are decrypted then.
import { type Cek, decryptEvent } from "../device/crypto";
import { bytesEqual, utf8 } from "../proto/bytes";
import type { ClientFrame, EncryptedEvent, IdBytes } from "../proto/frames";
import type { Proto } from "../proto/index";
import { bytesToUlid } from "../proto/ulid";
import type { ConversationState, EventContent, EventView, SegmentView } from "./model";

export type ConversationAction =
  | { type: "frame"; frame: ClientFrame }
  | { type: "cek"; segmentId: string; cek: Cek | undefined }
  /** A message this device sent: shown at once, and the relay's echo of it is then a duplicate. */
  | { type: "sent"; event: EventView };

export interface ReduceContext {
  proto: Proto;
  conversationId: IdBytes;
}

export function reduceConversation(ctx: ReduceContext, state: ConversationState, action: ConversationAction): ConversationState {
  switch (action.type) {
    case "frame":
      return onFrame(ctx, state, action.frame);
    case "cek":
      return onCek(ctx, state, action.segmentId, action.cek);
    case "sent":
      return hasEvent(state, action.event.segmentId, action.event.seq, action.event.direction)
        ? state
        : { ...state, events: [...state.events, action.event] };
  }
}

function onFrame(ctx: ReduceContext, state: ConversationState, frame: ClientFrame): ConversationState {
  switch (frame.type) {
    case "segment.open": {
      if (!bytesEqual(frame.conversation_id, ctx.conversationId)) return state;
      const id = bytesToUlid(frame.segment_id);
      const { type: _type, ...open } = frame;
      const existing = state.segments.get(id);
      const segment: SegmentView = { id, open, key: existing?.key ?? { kind: "pending" }, closedWith: existing?.closedWith };
      return { ...state, segments: new Map(state.segments).set(id, segment) };
    }
    case "event": {
      if (!bytesEqual(frame.event.header.conversation_id, ctx.conversationId)) return state;
      const segmentId = bytesToUlid(frame.event.header.segment_id);
      if (hasEvent(state, segmentId, frame.event.header.seq, frame.event.header.direction)) {
        return state; // replay after a reconnect may overlap, and our own sends come back
      }
      const key = state.segments.get(segmentId)?.key;
      const view: EventView = {
        segmentId,
        seq: frame.event.header.seq,
        type: frame.event.header.event_type,
        direction: frame.event.header.direction,
        meta: parseMeta(frame.meta),
        content: key?.kind === "unwrapped" ? decrypt(ctx.proto, key.cek, frame.event) : { kind: "locked" },
        raw: frame.event,
      };
      return { ...state, events: [...state.events, view] };
    }
    case "segment.close": {
      const id = bytesToUlid(frame.segment_id);
      const segment = state.segments.get(id);
      if (!segment) return state;
      return { ...state, segments: new Map(state.segments).set(id, { ...segment, closedWith: frame.event_count }) };
    }
    case "presence":
      return { ...state, live: frame.live.some((id) => bytesEqual(id, ctx.conversationId)) };
    default:
      return state;
  }
}

function hasEvent(state: ConversationState, segmentId: string, seq: number, direction: EventView["direction"]): boolean {
  return state.events.some((e) => e.segmentId === segmentId && e.seq === seq && e.direction === direction);
}

function onCek(ctx: ReduceContext, state: ConversationState, segmentId: string, cek: Cek | undefined): ConversationState {
  const segment = state.segments.get(segmentId);
  if (!segment) return state;
  const key: SegmentView["key"] = cek ? { kind: "unwrapped", cek } : { kind: "missing" };
  const events = cek
    ? state.events.map((e) =>
        e.segmentId === segmentId && e.content.kind === "locked" ? { ...e, content: decrypt(ctx.proto, cek, e.raw) } : e,
      )
    : state.events;
  return { ...state, segments: new Map(state.segments).set(segmentId, { ...segment, key }), events };
}

function decrypt(proto: Proto, cek: Cek, event: EncryptedEvent): EventContent {
  try {
    return { kind: "plaintext", text: utf8.decode(decryptEvent(proto, cek, event)) };
  } catch (e) {
    return { kind: "failed", reason: e instanceof Error ? e.message : String(e) };
  }
}

function parseMeta(meta: string): unknown {
  try {
    return JSON.parse(meta);
  } catch {
    return meta;
  }
}
